import { ArrowRight, RotateCcw, Timer } from 'lucide-react';
import { IncompleteBadge } from './IncompleteBadge';
import { SessionStatCard } from './SessionStatCard';
import { Button } from '@ai-tutor/ui';
import { OutcomeAuditList } from '@/components/features/mindspace/practice/session/OutcomeAuditList';
import {
  isReviewScope,
  type PracticeScope,
  type SessionOutcome,
} from '@/lib/practice/session-types';

interface SessionResultsProps {
  /** The RATED concepts. Every stat on this screen is over these. */
  outcomes: SessionOutcome[];
  /** Deck size. Exceeds `outcomes.length` when the run ended early. */
  total: number;
  /** The run ended before the deck did (Esc / End). The orchestrator passes
   *  the same verdict it records to history, so this screen and the history
   *  row can never disagree about whether a session was finished. Defaults
   *  to that same rule: fewer rated than the deck holds. */
  incomplete?: boolean;
  elapsedMs: number;
  /** Scope of the session that just finished - drives the CTA framing:
   *  Review scopes (due/weak) deplete + offer "Practice weak"; Practice
   *  scopes (starred/recent/custom) are repeatable + offer "Practice again". */
  scope: PracticeScope;
  onPracticeWeak: () => void;
  /** Repeat the same session with fresh questions. */
  onPracticeAgain: () => void;
  onBackToSetup: () => void;
}

const plural = (n: number, word: string) => `${word}${n === 1 ? '' : 's'}`;

function formatTime(elapsedMs: number): string {
  const minutes = Math.floor(elapsedMs / 60000);
  const seconds = Math.floor((elapsedMs % 60000) / 1000);
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export function SessionResults({
  outcomes,
  total,
  incomplete = outcomes.length < total,
  elapsedMs,
  scope,
  onPracticeWeak,
  onPracticeAgain,
  onBackToSetup,
}: SessionResultsProps) {
  const mastered = outcomes.filter(o => o.grade === 4).length;
  const improved = outcomes.filter(o => o.grade === 3).length;
  const weak = outcomes.filter(o => o.grade <= 2).length;
  const hasBreakdown = outcomes.length > 0;
  const masteryPct = hasBreakdown
    ? Math.round((mastered / outcomes.length) * 100)
    : null;

  return (
    <div className='mx-auto my-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-8 sm:py-10'>
      <ResultsHeader
        incomplete={incomplete}
        rated={outcomes.length}
        total={total}
        timeLabel={formatTime(elapsedMs)}
        masteryPct={masteryPct}
      />

      {hasBreakdown && (
        <div className='grid grid-cols-3 gap-3'>
          <SessionStatCard label='Mastered' value={mastered} tone='emerald' />
          <SessionStatCard label='Improved' value={improved} tone='indigo' />
          <SessionStatCard label='Weak' value={weak} tone='rose' />
        </div>
      )}

      <OutcomeAuditList outcomes={outcomes} defaultExpanded={false} />

      <div className='flex flex-col items-center gap-3'>
        {incomplete ? (
          <EndedEarlyActions
            weak={weak}
            onPracticeWeak={onPracticeWeak}
            onPracticeAgain={onPracticeAgain}
            onBackToSetup={onBackToSetup}
          />
        ) : isReviewScope(scope) ? (
          <ReviewActions
            weak={weak}
            onPracticeWeak={onPracticeWeak}
            onBackToSetup={onBackToSetup}
          />
        ) : (
          <PracticeAgainActions
            backLabel='Done'
            onPracticeAgain={onPracticeAgain}
            onBackToSetup={onBackToSetup}
          />
        )}
      </div>
    </div>
  );
}

function ResultsHeader({
  incomplete,
  rated,
  total,
  timeLabel,
  masteryPct,
}: {
  incomplete: boolean;
  rated: number;
  total: number;
  timeLabel: string;
  masteryPct: number | null;
}) {
  return (
    <header className='text-center'>
      <div className='flex items-center justify-center gap-2'>
        <h2 className='text-2xl font-semibold text-foreground'>
          {incomplete ? 'Ended early' : 'Session complete'}
        </h2>
        {incomplete && <IncompleteBadge />}
      </div>
      <div className='mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground'>
        <Timer className='h-3.5 w-3.5' />
        <span className='tabular-nums'>{timeLabel}</span>
        <span>·</span>
        <span>
          {incomplete
            ? `Practiced ${rated} of ${total} ${plural(total, 'concept')}`
            : `${total} ${plural(total, 'concept')}`}
        </span>
        {masteryPct != null && (
          <>
            <span>·</span>
            <span>{masteryPct}% mastered</span>
          </>
        )}
      </div>
    </header>
  );
}

function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline'
    >
      {label}
    </button>
  );
}

function PracticeWeakActions({
  weak,
  onPracticeWeak,
  onBackToSetup,
}: {
  weak: number;
  onPracticeWeak: () => void;
  onBackToSetup: () => void;
}) {
  return (
    <>
      <Button onClick={onPracticeWeak} size='lg' className='gap-2 max-sm:h-11'>
        Practice weak ({weak})
        <ArrowRight className='h-4 w-4' />
      </Button>
      <BackLink label='Back to setup' onClick={onBackToSetup} />
    </>
  );
}

// Practice (Starred / Recent / Custom), or any run that ended early:
// "Practice again" re-runs the same set with fresh quiz questions.
function PracticeAgainActions({
  backLabel,
  onPracticeAgain,
  onBackToSetup,
}: {
  backLabel: string;
  onPracticeAgain: () => void;
  onBackToSetup: () => void;
}) {
  return (
    <>
      <Button onClick={onPracticeAgain} size='lg' className='gap-2 max-sm:h-11'>
        <RotateCcw className='h-4 w-4' />
        Practice again
      </Button>
      <BackLink label={backLabel} onClick={onBackToSetup} />
    </>
  );
}

// Review (Due / Weak), finished: a remedial "Practice weak" pass if any were
// shaky, otherwise the queue is cleared.
function ReviewActions({
  weak,
  onPracticeWeak,
  onBackToSetup,
}: {
  weak: number;
  onPracticeWeak: () => void;
  onBackToSetup: () => void;
}) {
  if (weak > 0) {
    return (
      <PracticeWeakActions
        weak={weak}
        onPracticeWeak={onPracticeWeak}
        onBackToSetup={onBackToSetup}
      />
    );
  }
  return (
    <>
      <p className='text-sm text-muted-foreground'>
        Nice work, you&apos;ve cleared these.
      </p>
      <Button onClick={onBackToSetup} size='lg' className='gap-2 max-sm:h-11'>
        Back to setup
        <ArrowRight className='h-4 w-4' />
      </Button>
    </>
  );
}

// Ended early, whatever the scope: nothing was "cleared", so never say so.
// Weak ratings get the remedial pass; otherwise offer the same set again.
function EndedEarlyActions({
  weak,
  onPracticeWeak,
  onPracticeAgain,
  onBackToSetup,
}: {
  weak: number;
  onPracticeWeak: () => void;
  onPracticeAgain: () => void;
  onBackToSetup: () => void;
}) {
  if (weak > 0) {
    return (
      <PracticeWeakActions
        weak={weak}
        onPracticeWeak={onPracticeWeak}
        onBackToSetup={onBackToSetup}
      />
    );
  }
  return (
    <PracticeAgainActions
      backLabel='Back to setup'
      onPracticeAgain={onPracticeAgain}
      onBackToSetup={onBackToSetup}
    />
  );
}
